'use client';

/* eslint-disable @typescript-eslint/no-shadow */
import {
  useState,
  useCallback,
} from 'react';

import { httpGet } from '../api';
import {
  HttpClientRequestConfig,
  HttpClientResponse,
  HttpParams,
} from '../types';
import { composeUrlWithParams } from '../utils';
import { useApiConfig } from './useApiConfig';

type QueryArgs = Record<'queryParams' | 'pathParams', HttpParams>;
type QueryData<T> = {
  data: T | null;
  httpResponse: HttpClientResponse<T> | null;
};

export const useApiQuery = <TData>(
  url: string,
  httpClientRequestConfig?: HttpClientRequestConfig,
) => {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<QueryData<TData>>({
    httpResponse: null,
    data: null,
  });
  const [error, setError] = useState(null);
  const config = useApiConfig(httpClientRequestConfig);

  const query = useCallback(
    async ({ queryParams = {}, pathParams = {} }: Partial<QueryArgs> = {}) => {
      setIsLoading(true);
      setError(null);

      try {
        const composedUrl = composeUrlWithParams(url)({
          pathParams,
          queryParams,
        });

        const httpQueryResponse = await httpGet<TData>(composedUrl, config);
        setData({
          data: httpQueryResponse.data,
          httpResponse: httpQueryResponse,
        });
      } catch (error) {
        setError(error);
      } finally {
        setIsLoading(false);
      }
    },
     
    [url, config],
  );

  return {
    ...data,
    isLoading,
    error,
    query,
  };
};
